"use client";

import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { Form, FormField, FormControl, Button } from "@drpcrd/shared/ui";
import { ArrowUp } from "lucide-react";
import { ChatFileUpload } from "./chat-file-upload";
import { ChatLocalFileList } from "./chat-local-file-list";
import { cn, toast } from "@drpcrd/utils/shared";
import { formatFileSize } from "@drpcrd/utils/formatters";
import { OnSendAiMessageFn } from "./types";

// Must be < 4MB by server action Edge limitation, see here for more details:
// https://vercel.com/guides/how-to-bypass-vercel-body-size-limit-serverless-functions
export const MAX_FILE_SIZE = 4 * 1024 * 1024;

type ChatInputData = {
  message: string;
};

/**
 * Converts files to data URLs for sending with messages
 */
const convertFilesToDataURLs = async (files: FileList) =>
  Promise.all(
    Array.from(files).map(
      (file) =>
        new Promise<{
          type: "file";
          mediaType: string;
          url: string;
          filename: string;
        }>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => {
            resolve({
              type: "file",
              mediaType: file.type,
              url: reader.result as string,
              filename: file.name,
            });
          };
          reader.onerror = reject;
          reader.readAsDataURL(file);
        }),
    ),
  );

type ChatInputProps = {
  onSend?: OnSendAiMessageFn;
  onFilesChange?: (hasFiles: boolean) => void;
  placeholder?: string;
  defaultMessage?: string;
  disabled?: boolean;
  showFileUploader?: boolean;
};

export const ChatInput = ({
  onSend,
  onFilesChange,
  placeholder = "Type here",
  defaultMessage = "",
  disabled,
  showFileUploader = false,
}: ChatInputProps) => {
  const [files, setFiles] = useState<FileList | undefined>(undefined);

  const form = useForm<ChatInputData>({
    defaultValues: { message: defaultMessage },
    mode: "onSubmit",
  });

  const {
    handleSubmit,
    formState: { isSubmitting },
    control,
  } = form;

  const updateFiles = (newFiles: FileList | undefined) => {
    setFiles(newFiles);
    onFilesChange?.(newFiles != null && newFiles.length > 0);
  };

  const onRemoveFile = (index: number) => {
    if (!files) {
      return;
    }
    const dt = new DataTransfer();
    Array.from(files)
      .filter((_, i) => i !== index)
      .forEach((f) => dt.items.add(f));
    updateFiles(dt.files.length > 0 ? dt.files : undefined);
  };

  const onFileSelect = (selectedFiles: FileList | null) => {
    if (!selectedFiles) {
      return;
    }

    // Validate file types (PDF and images only)
    const validFiles = Array.from(selectedFiles).filter((file) => {
      const isValidType =
        file.type === "application/pdf" || file.type.startsWith("image/");

      if (!isValidType) {
        toast("Invalid file type", {
          description: "Only PDF and image files are supported.",
        });
        return false;
      }

      // Check file size
      if (file.size > MAX_FILE_SIZE) {
        toast("File too large", {
          description: `${file.name} is over ${formatFileSize(MAX_FILE_SIZE)}. Please choose a smaller file.`,
        });
        return false;
      }

      return true;
    });

    if (validFiles.length > 0) {
      const dt = new DataTransfer();
      validFiles.forEach((file) => dt.items.add(file));
      updateFiles(dt.files);
    }
  };

  const onSubmit = async (data: ChatInputData) => {
    const text = data.message.trim();
    if (!text && (!files || files.length === 0)) return;

    try {
      const fileParts =
        files && files.length > 0 ? await convertFilesToDataURLs(files) : [];

      if (onSend) {
        onSend(text, fileParts);
      }

      // Clear the form and files after sending
      form.reset({ message: "" });
      updateFiles(undefined);
    } catch {
      toast("Failed to send message", {
        description: "Please try again.",
      });
    }
  };

  return (
    <Form {...form}>
      <div className="mx-auto w-full">
        <ChatLocalFileList files={files} onRemoveFile={onRemoveFile} />

        <div className="max-h-16 rounded-3xl border border-neutral-500 bg-white p-2">
          <FormField
            control={control}
            name="message"
            render={({ field }) => (
              <FormControl>
                <textarea
                  {...field}
                  aria-label={placeholder}
                  placeholder={placeholder}
                  className="w-full resize-none border-none bg-transparent text-gray-700 placeholder-gray-400 focus:outline-none"
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      handleSubmit(onSubmit)();
                    }
                  }}
                />
              </FormControl>
            )}
          />
          <div
            className={cn(
              "flex",
              showFileUploader ? "justify-between" : "justify-end",
            )}
          >
            {showFileUploader && (
              <ChatFileUpload onFileSelect={onFileSelect} disabled={disabled} />
            )}
            <Button
              onClick={handleSubmit(onSubmit)}
              className="p-1"
              disabled={isSubmitting || disabled}
            >
              <ArrowUp />
            </Button>
          </div>
        </div>
      </div>
    </Form>
  );
};
